import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DatePickerProps {
  value: string; // DD/MM/YYYY
  onChange: (date: string) => void; // DD/MM/YYYY
  disabled?: boolean;
  placeholder?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, disabled = false, placeholder = 'Selecione uma data' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [inputValue, setInputValue] = useState(value || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Converter DD/MM/YYYY para Date
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  // Converter Date para DD/MM/YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const selectedDate = parseDate(value);

  // Atualizar valor do input quando value mudar
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Atualizar mês atual quando data selecionada muda
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [value]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gerar dias do mês
  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  // Preencher dias do início (dias do mês anterior)
  const getStartPadding = () => {
    const start = startOfMonth(currentMonth);
    return getDay(start); // 0 = domingo, 1 = segunda, etc.
  };

  const handleDateSelect = (date: Date) => {
    const formatted = formatDate(date);
    onChange(formatted);
    setInputValue(formatted);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    const today = new Date();
    const formatted = formatDate(today);
    onChange(formatted);
    setInputValue(formatted);
    setCurrentMonth(today);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^0-9]/g, '');

    // Auto-formatar DD/MM/YYYY
    if (input.length >= 2) {
      input = input.slice(0, 2) + '/' + input.slice(2);
    }
    if (input.length >= 5) {
      input = input.slice(0, 5) + '/' + input.slice(5);
    }
    if (input.length > 10) {
      input = input.slice(0, 10);
    }

    setInputValue(input);

    // Validar e atualizar apenas se formato completo
    if (input.length === 10) {
      const [day, month, year] = input.split('/').map(Number);
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
        onChange(input);
      }
    }
  };

  const handleInputBlur = () => {
    // Se input vazio, limpar value
    if (!inputValue) {
      onChange('');
    }
  };

  const daysInMonth = getDaysInMonth();
  const startPadding = getStartPadding();
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input Field - Agora editável */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={10}
          className="w-full px-3 py-3 pr-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <Calendar className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-2xl p-4 w-80">
          {/* Header - Navegação de mês */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
            </h3>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Dias da Semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Dias do Mês */}
          <div className="grid grid-cols-7 gap-1">
            {/* Padding para início do mês */}
            {Array.from({ length: startPadding }).map((_, i) => (
              <div key={`padding-${i}`} className="h-10"></div>
            ))}

            {/* Dias do mês */}
            {daysInMonth.map((day) => {
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isCurrentDay = isToday(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  className={`
                    h-10 rounded-lg text-sm font-medium transition-all transform hover:scale-110
                    ${isSelected
                      ? 'bg-blue-600 text-white shadow-lg ring-2 ring-blue-300'
                      : isCurrentDay
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Botão Hoje */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={handleToday}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-semibold transition shadow-md"
            >
              📅 Hoje - {formatDate(new Date())}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
